import { useEffect } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import CloseButton from "../CloseButton/CloseButton";
import {
  useApiHealth,
  refreshHealth,
  type EndpointHealth,
  type HealthSnapshot,
} from "../../api/apiHealthService";
import styles from "./ApiStatusModal.module.css";

/* ------------------------------------------------------------------ */
/*  Re-export for App.tsx consumption                                  */
/* ------------------------------------------------------------------ */

export type ServiceStatus = {
  name: string;
  source: "api" | "seed" | "ls" | "none" | "loading";
  count?: number | null;
  /** Optional formatted label (e.g. "1,247 files · 2.3 GB") — overrides numeric count display */
  countLabel?: string;
  loading: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  services: ServiceStatus[];
  apiBaseUrl: string;
  onRefresh: () => void;
};

/* ------------------------------------------------------------------ */
/*  Status badges (data source)                                        */
/* ------------------------------------------------------------------ */

function StatusBadge({ source, loading }: { source: ServiceStatus["source"]; loading: boolean }) {
  if (loading) {
    return (
      <span className={`${styles.statusBadge} ${styles.statusLoading}`}>
        <span className={`${styles.dot} ${styles.dotGray}`} />
        Loading...
      </span>
    );
  }

  switch (source) {
    case "api":
      return (
        <span className={`${styles.statusBadge} ${styles.statusApi}`}>
          <span className={`${styles.dot} ${styles.dotGreen}`} />
          API
        </span>
      );
    case "seed":
      return (
        <span className={`${styles.statusBadge} ${styles.statusSeed}`}>
          <span className={`${styles.dot} ${styles.dotAmber}`} />
          Seed (offline)
        </span>
      );
    case "ls":
      return (
        <span className={`${styles.statusBadge} ${styles.statusLs}`}>
          <span className={`${styles.dot} ${styles.dotBlue}`} />
          localStorage
        </span>
      );
    case "none":
      return (
        <span className={`${styles.statusBadge} ${styles.statusNone}`}>
          <span className={`${styles.dot} ${styles.dotGray}`} />
          Fallback (LS)
        </span>
      );
    default:
      return (
        <span className={`${styles.statusBadge} ${styles.statusNone}`}>
          <span className={`${styles.dot} ${styles.dotGray}`} />
          --
        </span>
      );
  }
}

/* ------------------------------------------------------------------ */
/*  Health status badge                                                */
/* ------------------------------------------------------------------ */

function HealthBadge({ ep }: { ep: EndpointHealth }) {
  if (ep.status === "checking" || ep.status === "unknown") {
    return (
      <span className={`${styles.statusBadge} ${styles.statusLoading}`}>
        <span className={`${styles.dot} ${styles.dotGray}`} />
        {ep.status === "checking" ? "Checking..." : "Unknown"}
      </span>
    );
  }

  if (ep.status === "up") {
    return (
      <span className={`${styles.statusBadge} ${styles.statusApi}`}>
        <span className={`${styles.dot} ${styles.dotGreen}`} />
        Up
      </span>
    );
  }

  if (ep.status === "degraded") {
    return (
      <span className={`${styles.statusBadge} ${styles.statusSeed}`}>
        <span className={`${styles.dot} ${styles.dotAmber}`} />
        Slow
      </span>
    );
  }

  // down
  return (
    <span className={`${styles.statusBadge} ${styles.statusDown}`}>
      <span className={`${styles.dot} ${styles.dotRed}`} />
      Down
    </span>
  );
}

function formatMs(ms: number | null): string {
  if (ms == null) return "--";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function overallLabel(status: HealthSnapshot["overallStatus"]): string {
  switch (status) {
    case "healthy": return "All Services Online";
    case "degraded": return "Some Services Degraded";
    case "down": return "Services Offline";
    case "checking": return "Checking Services...";
  }
}

function overallDotClass(status: HealthSnapshot["overallStatus"]): string {
  switch (status) {
    case "healthy": return styles.dotGreen;
    case "degraded": return styles.dotAmber;
    case "down": return styles.dotRed;
    case "checking": return styles.dotGray;
  }
}

/* ------------------------------------------------------------------ */
/*  Modal component                                                    */
/* ------------------------------------------------------------------ */

export default function ApiStatusModal({ isOpen, onClose, services, apiBaseUrl, onRefresh }: Props) {
  const { t } = useAppTranslation('pages');
  const health = useApiHealth();

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleRefreshAll = () => {
    onRefresh();
    refreshHealth();
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            <span className={`${styles.dot} ${styles.overallDot} ${overallDotClass(health.overallStatus)}`} />
            {overallLabel(health.overallStatus)}
          </h3>
          <CloseButton onClick={onClose} size="md" />
        </div>

        <div className={styles.body}>
          {/* ── Service Health ──────────────────────────────────── */}
          <div className={styles.sectionLabel}>Service Health</div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>Status</th>
                <th>Latency</th>
                <th>Code</th>
              </tr>
            </thead>
            <tbody>
              {health.endpoints.map((ep) => (
                <tr key={ep.name}>
                  <td className={styles.serviceName}>{ep.name}</td>
                  <td><HealthBadge ep={ep} /></td>
                  <td className={styles.latencyCell}>
                    {ep.status === "checking" || ep.status === "unknown"
                      ? "..."
                      : formatMs(ep.responseMs)}
                  </td>
                  <td className={styles.countCell}>
                    {ep.statusCode ?? "--"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {health.lastPollAt && (
            <div className={styles.lastChecked}>
              Last checked: {new Date(health.lastPollAt).toLocaleTimeString()}
            </div>
          )}

          {/* ── Data Sources ───────────────────────────────────── */}
          <div className={`${styles.sectionLabel} ${styles.sectionLabelSpaced}`}>Data Sources</div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('apiStatus.service')}</th>
                <th>{t('apiStatus.source')}</th>
                <th>{t('apiStatus.rows')}</th>
              </tr>
            </thead>
            <tbody>
              {services.map((svc) => (
                <tr key={svc.name}>
                  <td className={styles.serviceName}>{svc.name}</td>
                  <td>
                    <StatusBadge source={svc.source} loading={svc.loading} />
                  </td>
                  <td className={styles.countCell}>
                    {svc.loading
                      ? "..."
                      : svc.countLabel
                        ? svc.countLabel
                        : svc.count != null
                          ? svc.count.toLocaleString()
                          : "--"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.footer}>
          <span className={styles.apiBase}>{apiBaseUrl}</span>
          <button className={styles.refreshBtn} onClick={handleRefreshAll}>
            Refresh All
          </button>
        </div>
      </div>
    </div>
  );
}
